/**
 * Immersive desktop side panel for the header menu.
 * - Hovering a top-level item (or activating its hidden disclosure button) opens the panel
 *   below the header and shows that item's view. Hovering another item switches the view.
 * - Second-level items with children slide to their third level; "Back" slides back.
 * - Hovering or focusing any link swaps the feature image with a crossfade.
 * - Closes when the pointer leaves the header and panel, on overlay click, close button or Escape.
 */
if (!window.__sidePanelMenuInitialized) {
  window.__sidePanelMenuInitialized = true;

  const OPEN_DELAY_MS = 120;
  const CLOSE_DELAY_MS = 300;
  const canHover = window.matchMedia('(hover: hover) and (pointer: fine)');

  /** @type {HTMLElement | null} */
  let panel = null;
  /** @type {string | null} */
  let activeIndex = null;
  /** @type {HTMLElement | null} */
  let triggerItem = null;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  let openTimer;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  let closeTimer;
  /** @type {string | null} */
  let currentImage = null;

  const isOpen = () => Boolean(panel?.matches(':popover-open'));

  /**
   * @param {HTMLElement} item
   * @returns {HTMLElement | null}
   */
  const panelFor = (item) => {
    const id = item.querySelector('[data-sidepanel-toggle]')?.getAttribute('aria-controls');
    return id ? document.getElementById(id) : null;
  };

  const positionPanel = () => {
    if (!panel || !triggerItem) return;
    const header = triggerItem.closest('header-component') ?? triggerItem.closest('header');
    const top = Math.max(0, header ? header.getBoundingClientRect().bottom : 0);
    panel.style.setProperty('--side-menu-top', `${top}px`);
  };

  /**
   * @param {HTMLElement} view
   * @param {string} state
   */
  const setLevelState = (view, state) => {
    for (const level of view.querySelectorAll('[data-sidepanel-level]')) {
      const el = /** @type {HTMLElement} */ (level);
      const isRoot = el.dataset.sidepanelLevel === 'root';
      let levelState;
      if (state === 'root') {
        levelState = isRoot ? 'active' : 'next';
      } else {
        levelState = isRoot ? 'prev' : el.dataset.sidepanelLevel === state ? 'active' : 'next';
      }
      el.dataset.state = levelState;
      el.inert = levelState !== 'active';
      if (levelState === 'active') el.scrollTop = 0;
    }
    for (const drill of view.querySelectorAll('[data-sidepanel-drill]')) {
      drill.setAttribute('aria-expanded', String(/** @type {HTMLElement} */ (drill).dataset.sidepanelDrill === state));
    }
  };

  /**
   * Crossfade the feature image to the one described by the element's data attributes.
   * @param {HTMLElement | null} source
   */
  const showImage = (source) => {
    if (!panel || !source) return;
    const src = source.dataset.image;
    const caption = /** @type {HTMLAnchorElement | null} */ (panel.querySelector('[data-sidepanel-caption]'));
    const captionTitle = panel.querySelector('[data-sidepanel-caption-title]');

    if (caption && source.dataset.imageUrl) caption.href = source.dataset.imageUrl;
    if (captionTitle && source.dataset.imageTitle) captionTitle.textContent = source.dataset.imageTitle;

    if (!src || src === currentImage) return;
    currentImage = src;

    const layers = /** @type {HTMLImageElement[]} */ ([...panel.querySelectorAll('[data-sidepanel-image]')]);
    const visible = layers.find((layer) => layer.hasAttribute('data-active'));
    const next = layers.find((layer) => layer !== visible) ?? layers[0];
    if (!next) return;

    const reveal = () => {
      if (currentImage !== src) return;
      next.setAttribute('data-active', '');
      visible?.removeAttribute('data-active');
    };

    next.removeAttribute('data-active');
    if (source.dataset.imageSrcset) next.srcset = source.dataset.imageSrcset;
    next.src = src;
    if (next.complete) {
      requestAnimationFrame(reveal);
    } else {
      next.addEventListener('load', reveal, { once: true });
    }
  };

  /**
   * @param {string} index
   */
  const showView = (index) => {
    if (!panel) return;
    activeIndex = index;
    for (const view of panel.querySelectorAll('[data-sidepanel-view]')) {
      const el = /** @type {HTMLElement} */ (view);
      const isActive = el.dataset.sidepanelView === index;
      el.hidden = !isActive;
      if (isActive) {
        setLevelState(el, 'root');
        showImage(el);
      }
    }
    for (const item of document.querySelectorAll('[data-sidepanel-item]')) {
      const el = /** @type {HTMLElement} */ (item);
      el.querySelector('[data-sidepanel-toggle]')?.setAttribute(
        'aria-expanded',
        String(isOpen() && el.dataset.sidepanelItem === index)
      );
    }
  };

  /**
   * @param {HTMLElement} item
   */
  const open = (item) => {
    const targetPanel = panelFor(item);
    if (!targetPanel) return;
    clearTimeout(closeTimer);

    if (panel && panel !== targetPanel && isOpen()) panel.hidePopover();
    panel = targetPanel;
    triggerItem = item;
    positionPanel();

    if (!isOpen()) panel.showPopover();
    showView(item.dataset.sidepanelItem ?? '');
  };

  const close = () => {
    clearTimeout(openTimer);
    clearTimeout(closeTimer);
    if (!panel || !isOpen()) return;
    panel.hidePopover();
    activeIndex = null;
    for (const toggle of document.querySelectorAll('[data-sidepanel-toggle]')) {
      toggle.setAttribute('aria-expanded', 'false');
    }
  };

  const scheduleClose = () => {
    clearTimeout(openTimer);
    clearTimeout(closeTimer);
    closeTimer = setTimeout(close, CLOSE_DELAY_MS);
  };

  // Hover intent
  document.addEventListener('pointerover', (event) => {
    if (!canHover.matches || event.pointerType === 'touch') return;
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const item = /** @type {HTMLElement | null} */ (target.closest('[data-sidepanel-item]'));
    if (item) {
      clearTimeout(closeTimer);
      clearTimeout(openTimer);
      if (isOpen()) {
        if (item.dataset.sidepanelItem !== activeIndex) open(item);
      } else {
        openTimer = setTimeout(() => open(item), OPEN_DELAY_MS);
      }
      return;
    }

    if (!isOpen() || !panel) {
      clearTimeout(openTimer);
      return;
    }

    const sheet = panel.querySelector('[data-sidepanel-sheet]');
    if (sheet?.contains(target)) {
      clearTimeout(closeTimer);
      const source = /** @type {HTMLElement | null} */ (target.closest('[data-image-title]'));
      if (source && !source.matches('[data-sidepanel-view]')) showImage(source);
      return;
    }

    scheduleClose();
  });

  document.documentElement.addEventListener('pointerleave', () => {
    if (isOpen()) scheduleClose();
  });

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    // Keyboard / assistive tech toggle
    const toggle = target.closest('[data-sidepanel-toggle]');
    if (toggle) {
      const item = /** @type {HTMLElement | null} */ (toggle.closest('[data-sidepanel-item]'));
      if (!item) return;
      if (isOpen() && item.dataset.sidepanelItem === activeIndex) {
        close();
      } else {
        open(item);
        const firstLink = panel?.querySelector(
          '[data-sidepanel-view]:not([hidden]) [data-state="active"] .side-menu__link'
        );
        if (firstLink instanceof HTMLElement) firstLink.focus();
      }
      return;
    }

    if (!panel || !isOpen()) return;

    if (target.closest('[data-sidepanel-close]') || target === panel) {
      const button = triggerItem?.querySelector('[data-sidepanel-toggle]');
      close();
      if (button instanceof HTMLElement && event.detail === 0) button.focus();
      return;
    }

    const drill = /** @type {HTMLElement | null} */ (target.closest('[data-sidepanel-drill]'));
    if (drill) {
      const view = drill.closest('[data-sidepanel-view]');
      if (view instanceof HTMLElement && drill.dataset.sidepanelDrill) {
        setLevelState(view, drill.dataset.sidepanelDrill);
        showImage(drill);
        if (event.detail === 0) {
          const firstLink = view.querySelector('[data-state="active"] .side-menu__back');
          if (firstLink instanceof HTMLElement) requestAnimationFrame(() => firstLink.focus());
        }
      }
      return;
    }

    const back = target.closest('[data-sidepanel-back]');
    if (back) {
      const view = back.closest('[data-sidepanel-view]');
      if (view instanceof HTMLElement) {
        const previous = view.querySelector(`[data-sidepanel-drill="${back.closest('[data-sidepanel-level]')?.getAttribute('data-sidepanel-level')}"]`);
        setLevelState(view, 'root');
        showImage(view);
        if (event.detail === 0 && previous instanceof HTMLElement) requestAnimationFrame(() => previous.focus());
      }
    }
  });

  // Swap the image on keyboard focus too
  document.addEventListener('focusin', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target || !panel || !isOpen()) return;

    if (panel.contains(target)) {
      const source = /** @type {HTMLElement | null} */ (target.closest('[data-image-title]'));
      if (source && !source.matches('[data-sidepanel-view]')) showImage(source);
      return;
    }

    if (!target.closest('[data-sidepanel-item]')) close();
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape' || !isOpen()) return;
    const button = triggerItem?.querySelector('[data-sidepanel-toggle]');
    close();
    if (button instanceof HTMLElement) button.focus();
  });

  window.addEventListener('resize', positionPanel, { passive: true });
  window.addEventListener('scroll', positionPanel, { passive: true });
}
